import { useContext } from 'react';

import { useQueryClient, useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import { ApiRequestProps } from '../../../common/interface';
import type { Collection, CollectionItem } from '../../../common/type';
import {
  COLLECTION_ITEM_MUTATE_KEY,
  COLLECTION_ITEM_QUERY_KEY,
  COLLECTION_LIST_QUERY_KEY,
  COLLECTION_QUERY_KEY,
} from '../constants';

type InternalCollectionItems = Array<CollectionItem & { id?: string }>;
type Props = ApiRequestProps & {
  id: string;
  provider?: string;
  designation?: string;
};

export const useAddCollectionItem = (params: Props) => {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { baseURL, id, designation } = params;
  const { access_token, userProfile } = useContext(LoginContext);

  const parsedBaseURL = baseURL || COMMUNITY_FUNCTIONS_URL;

  const axiosConfig = {
    baseURL: `${parsedBaseURL}/store/v1/collections`,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
  };
  const api = axios.create(axiosConfig);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [COLLECTION_ITEM_MUTATE_KEY, parsedBaseURL, id],
    mutationFn: async (changedItems: InternalCollectionItems) => {
      return Promise.all(
        changedItems.map(item => {
          const { id: inlineid } = item;
          return api.post(`/${inlineid || id}/items`, item, {
            params: {
              provider: 'aws',
            },
          });
        })
      );
    },
    onMutate: async (changedItems: InternalCollectionItems) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [COLLECTION_QUERY_KEY, parsedBaseURL, id],
      });

      await queryClient.cancelQueries({
        queryKey: [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
      });

      await queryClient.cancelQueries({
        queryKey: [COLLECTION_ITEM_QUERY_KEY, parsedBaseURL, id],
      });

      const savedCollection = queryClient.getQueryData([
        COLLECTION_QUERY_KEY,
        parsedBaseURL,
        id,
      ]);

      const savedItems = queryClient.getQueryData([
        COLLECTION_ITEM_QUERY_KEY,
        parsedBaseURL,
        id,
      ]);

      const savedList = queryClient.getQueryData([
        COLLECTION_LIST_QUERY_KEY,
        parsedBaseURL,
        userProfile?.sub,
      ]);

      queryClient.setQueryData<Collection | undefined>(
        [COLLECTION_QUERY_KEY, parsedBaseURL, id],
        old => {
          if (!old) {
            return old;
          }
          const newItems = { ...old };
          changedItems.forEach(item => {
            const index = newItems.collection_items.findIndex(
              i => i.item_key === item.item_key
            );
            if (index >= 0) {
              newItems.collection_items[index] = item;
            } else {
              newItems.collection_items.push(item);
            }
          });

          return newItems;
        }
      );

      queryClient.setQueryData<InternalCollectionItems | undefined>(
        [COLLECTION_ITEM_QUERY_KEY, parsedBaseURL, id],
        old => {
          if (!old || old?.length) {
            return old;
          }
          const newItems = [...(old || [])];
          changedItems.forEach(item => {
            const index = newItems.findIndex(i => i.item_key === item.item_key);
            if (index >= 0) {
              newItems[index] = item;
            } else {
              newItems.push(item);
            }
          });

          return newItems;
        }
      );

      queryClient.setQueryData<Collection[] | undefined>(
        [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
        old => {
          if (!old || !old?.length) {
            return old;
          }
          const newItems = [...(old || [])];

          changedItems.forEach(item => {
            const { id: inlineid } = item;
            const collectionId = inlineid || id;
            const currentCollectionIndex = newItems.findIndex(
              collection => collection.id === collectionId
            );
            if (currentCollectionIndex < 0) {
              newItems.push({
                isOptimistic: true,
                id: collectionId,
                collection_items: [
                  {
                    item_key: item.item_key,
                  },
                ],
                title: 'Bookmarks',
                date_created: Math.floor(Date.now() / 1000),
                date_modified: Math.floor(Date.now() / 1000),
                creator_id: userProfile?.sub || '',
                designation,
              });
            } else {
              newItems[currentCollectionIndex] = {
                ...newItems[currentCollectionIndex],
                isOptimistic: true,
                collection_items: [
                  ...(newItems[currentCollectionIndex].collection_items || []),
                  item,
                ],
              };
            }
          });
          return newItems;
        }
      );

      return { savedCollection, savedItems, savedList };
    },
    onError: async (error, variables, context) => {
      const { savedCollection, savedItems, savedList } = context || {};
      queryClient.setQueryData(
        [COLLECTION_QUERY_KEY, parsedBaseURL, id],
        savedCollection
      );
      queryClient.setQueryData(
        [COLLECTION_ITEM_QUERY_KEY, parsedBaseURL, id],
        savedItems
      );
      queryClient.setQueryData(
        [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
        savedList
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_LIST_QUERY_KEY, parsedBaseURL, userProfile?.sub],
      });
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEY, parsedBaseURL, id],
      });
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_ITEM_QUERY_KEY, parsedBaseURL, id],
      });
    },
  });
};
